import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Settings as SettingsIcon, LogOut, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CRMPortal = ({ user }) => {
  const navigate = useNavigate();
  const enconOrange = '#ee7203';
  const enconBlack = '#1d1d1b';
  const enconGrey = '#9d9d9c';
  const enconLightGrey = '#ededed';
  
  const [embedMode, setEmbedMode] = useState('trying'); // 'trying', 'success', 'failed'
  const iframeRef = useRef(null);
  const checkCount = useRef(0);

  useEffect(() => {
    // Check multiple times to see if iframe actually has content
    const checkIframeContent = () => {
      checkCount.current += 1;
      
      try {
        // Try to access iframe content (will fail if blocked by CORS/X-Frame-Options)
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
          // Try to access the document - this will throw an error if blocked
          const iframeDoc = iframe.contentWindow.document;
          if (iframeDoc && iframeDoc.body && iframeDoc.body.innerHTML.length > 0) {
            console.log('✅ Iframe has content - embedding successful');
            setEmbedMode('success');
            return;
          }
        }
      } catch (e) {
        // This is actually expected - cross-origin will throw error even when content loads
        console.log('Cross-origin restriction (expected)');
      }
      
      // After 3 checks (3 seconds), if still trying, assume iframe is blocked
      if (checkCount.current >= 3 && embedMode === 'trying') {
        console.log('📱 Iframe appears blocked - showing click-to-open button');
        setEmbedMode('failed');
      } else if (embedMode === 'trying' && checkCount.current < 3) {
        // Check again in 1 second
        setTimeout(checkIframeContent, 1000);
      }
    };

    // Start checking after initial mount
    const initialTimeout = setTimeout(checkIframeContent, 1000);

    return () => {
      clearTimeout(initialTimeout);
    };
  }, [embedMode]);

  const openInNewTab = () => {
    window.open('https://crm.zoho.eu/crm/encon/tab/Home/begin', '_blank', 'noopener,noreferrer');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    toast.success('Logged out');
    navigate('/login');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: enconLightGrey }}>
      {/* Header - Matching Dashboard Style */}
      <div className="bg-white shadow-md border-b-2 sticky top-0 z-40" style={{ borderBottomColor: enconOrange }}>
        <div className="px-2 md:px-4 lg:px-8 py-2 md:py-4">
          <div className="flex items-center justify-between gap-1 md:gap-4">
            {/* Left: Home + Title */}
            <div className="flex items-center gap-1 md:gap-3">
              <button
                onClick={() => navigate('/')}
                className="h-8 w-8 md:h-10 md:w-10 luxury-control hover:shadow-lg flex items-center justify-center"
                style={{ color: enconOrange }}
                title="Home"
              >
                <Home className="w-4 h-4 md:w-5 md:h-5" />
              </button>
              
              <div>
                <h1 className="text-sm md:text-lg lg:text-2xl font-bold whitespace-nowrap" style={{ color: enconBlack }}>
                  CRM Portal
                </h1>
                <p className="text-[10px] md:text-xs lg:text-sm mt-0.5" style={{ color: enconGrey }}>
                  Welcome, <span className="font-semibold" style={{ color: enconOrange }}>{user?.username}</span>
                </p>
              </div>
            </div>

            {/* Right: Open in New Tab + Settings + Logout */}
            <div className="flex items-center gap-1 md:gap-2">
              <button
                onClick={openInNewTab}
                className="px-3 md:px-4 py-2 rounded-lg font-bold transition-all hover:scale-105 flex items-center gap-2 shadow-lg"
                style={{ backgroundColor: enconOrange, color: 'white' }}
                title="Open CRM in new tab (recommended)"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="text-xs md:text-sm">Open in New Tab</span>
              </button>

              {(user?.role === 'super_admin' || user?.can_access_settings) && (
                <button
                  onClick={() => navigate('/settings')}
                  className="h-8 w-8 md:h-10 md:w-10 luxury-control hover:shadow-lg flex items-center justify-center"
                  style={{ color: enconOrange }}
                  title="Settings"
                >
                  <SettingsIcon className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              )}
              
              <button
                onClick={handleLogout}
                className="h-8 w-8 md:h-10 md:w-10 luxury-control hover:shadow-lg flex items-center justify-center"
                style={{ color: enconOrange }}
                title="Logout"
              >
                <LogOut className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area - Smart Embed with Auto-Redirect */}
      {embedMode === 'trying' || embedMode === 'success' ? (
        <div className="relative w-full" style={{ height: 'calc(100vh - 80px)' }}>
          {/* Loading Overlay - Show while trying */}
          {embedMode === 'trying' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
              <Loader2 className="w-12 h-12 animate-spin mb-4" style={{ color: enconOrange }} />
              <p className="text-lg font-semibold" style={{ color: enconBlack }}>
                Loading CRM Portal...
              </p>
              <p className="text-sm mt-2" style={{ color: enconGrey }}>
                Checking browser compatibility...
              </p>
            </div>
          )}
          
          {/* Iframe - Try to Embed */}
          <iframe
            ref={iframeRef}
            src="https://crm.zoho.eu/crm/encon/tab/Home/begin"
            className="w-full h-full border-0"
            title="CRM Portal"
            onLoad={() => {
              console.log('Iframe element loaded (checking for content...)');
            }}
            onError={() => {
              console.log('❌ Iframe error event fired');
              setEmbedMode('failed');
            }}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation"
            allow="clipboard-read; clipboard-write"
          />
        </div>
      ) : (
        /* Fallback - Show clean button to open CRM (no popup blockers!) */
        <div className="flex flex-col items-center justify-center p-4 md:p-8" style={{ minHeight: 'calc(100vh - 100px)', backgroundColor: enconLightGrey }}>
          <div className="max-w-3xl w-full bg-white rounded-lg shadow-xl border-4 p-6 md:p-10" style={{ borderColor: enconOrange }}>
            <div className="text-center">
              {/* Logo */}
              <img 
                src="/crm-logo.png" 
                alt="CRM" 
                className="w-40 h-40 mx-auto mb-6 object-contain"
              />
              
              {/* Title */}
              <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: enconBlack }}>
                Zoho CRM Portal
              </h2>
              
              <p className="text-base md:text-lg mb-8" style={{ color: enconGrey }}>
                Access your CRM system, manage contacts, and track sales
              </p>
              
              {/* Main Action Button - Regular link, no popup blockers! */}
              <a
                href="https://crm.zoho.eu/crm/encon/tab/Home/begin"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 w-full px-8 py-5 rounded-xl font-bold transition-all hover:scale-105 hover:shadow-2xl text-xl"
                style={{ backgroundColor: enconOrange, color: 'white', textDecoration: 'none' }}
              >
                <ExternalLink className="w-7 h-7" />
                Open CRM Portal
              </a>

              {/* Info Box */}
              <div className="p-5 rounded-lg mt-6 mb-6" style={{ backgroundColor: enconLightGrey }}>
                <p className="text-sm md:text-base" style={{ color: enconGrey }}>
                  The CRM will open in a new browser tab
                </p>
              </div>

              {/* URL Display */}
              <div className="text-xs p-4 rounded-lg border-2" style={{ borderColor: enconOrange, backgroundColor: '#fff7ed' }}>
                <p className="mb-2 font-semibold" style={{ color: enconBlack }}>CRM Address:</p>
                <a 
                  href="https://crm.zoho.eu/crm/encon/tab/Home/begin" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="underline break-all font-mono"
                  style={{ color: enconOrange }}
                >
                  crm.zoho.eu/crm/encon
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMPortal;
